import styled, { css } from "styled-components"

import { CENTER, COLLAPSIBLE_COLS, COLLAPSIBLE_NONE, COLLAPSIBLE_ROWS, LEFT, RIGHT } from "./utils"

export const TableContainer = styled.div`
  table {
    border-collapse: collapse;
  }
  * {
    box-sizing: border-box;
  }

  ${getAlignment}
  ${getBordered}
  ${getScrollable}
  ${getAutoFit}
  ${getCollapsible}
`

function getAlignment({ $contentAlignment }) {
  switch ($contentAlignment) {
    case LEFT:
      return css`
        td,
        th {
          text-align: left;
        }
      `
    case RIGHT:
      return css`
        td,
        th {
          text-align: right;
        }
      `
    case CENTER:
      return css`
        td,
        th {
          text-align: center;
        }
      `
    default:
      return css`
        td,
        th {
          text-align: left;
        }
      `
  }
}

function getBordered({ theme, $borderVariant, $bordered, $colLines }) {
  if (!$bordered || $colLines) return

  const { inline } = theme.borders[$borderVariant]

  return css`
    border: ${inline};
  `
}

function getScrollable({ $verticallyScrollable, $horizontallyScrollable, $scrollHeight }) {
  let style = css``

  if ($verticallyScrollable) {
    style = css`
      ${style}
      height: ${$scrollHeight};
      overflow-y: auto;
      table {
        position: relative;
        border-collapse: separate;
        border-spacing: 0;
        thead th {
          position: sticky;
          top: 0;
        }
      }
    `
  }

  if ($horizontallyScrollable) {
    style = css`
      ${style}
      min-width: 100%;
      width: auto;
      overflow-x: auto;
      table {
        width: 100%;
        overflow: visible;
        white-space: nowrap;
      }
    `
  }

  if (!$horizontallyScrollable && !$verticallyScrollable) {
    style = css`
      ${style}
      table {
        border-collapse: collapse;
      }
    `
  }

  return style
}

function getAutoFit({ $autoFit }) {
  if ($autoFit) {
    return
  }

  return css`
    table {
      table-layout: fixed;
      width: 100%;
      td,
      th {
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  `
}

function getCollapsible({ $collapsible }) {
  if ($collapsible === COLLAPSIBLE_NONE) return

  return css`
    table {
      display: block;
      thead {
        display: none;
      }
      tbody {
        display: block;
      }
      tr,
      td,
      th {
        width: 100%;
        display: block;
      }
    }
  `
}

export const Row = styled.tr`
  ${getHeader}
  ${getStripped}
  ${getSelectable}
  ${getSelected}
  ${getBorders}
  ${getScrollableForRow}
  ${getCollapsibleForRow}
`

Row.defaultProps = {
  className: "tsr-data-table-row",
}

function getHeader({ $isHeader, $headerVariant, theme }) {
  if (!$isHeader) return

  const { bg, fg } = theme.colors[$headerVariant]

  return css`
    & > * {
      background-color: ${bg};
      color: ${fg};
    }
  `
}

function getStripped({ $isHeader, $stripped, $strippedVariant, theme }) {
  if ($isHeader || !$stripped) return

  const { fg, bg } = theme.colors[$strippedVariant]

  return css`
    &:nth-child(even) > * {
      background-color: ${bg};
      color: ${fg};
    }
  `
}

function getSelectable({ $isHeader, $selectable, $hoverVariant, theme }) {
  if ($isHeader || !$selectable) return

  const { fg, bg } = theme.colors[$hoverVariant]

  return css`
    &:hover > * {
      background-color: ${bg};
      color: ${fg};
    }
  `
}
function getSelected({ selected, $selectable, theme, $selectedVariant }) {
  const { fg, bg } = theme.colors[$selectedVariant]
  let style = css``

  if (selected) {
    style = css`
      ${style}
      & > * {
        background-color: ${bg};
        color: ${fg};
      }
    `
  }

  if ($selectable) {
    style = css`
      ${style}
      &:hover > * {
        background-color: ${bg};
        color: ${fg};
        cursor: pointer;
      }
    `
  }

  return style
}

function getBorders({ $rowLines, $colLines, theme, $borderVariant, $collapsible }) {
  const collapsed = $collapsible === COLLAPSIBLE_ROWS || $collapsible === COLLAPSIBLE_COLS
  const { inline } = theme.borders[$borderVariant]

  if (collapsed) {
    return
  }

  if ($colLines) {
    return css`
      & > * {
        border: ${inline};
      }
    `
  }

  if ($rowLines) {
    return css`
      border-bottom: ${inline};

      &:last-child {
        border-bottom: none;
      }
    `
  }
}

function getScrollableForRow({ $horizontallyScrollable, $verticallyScrollable, theme, $borderVariant, $numCols }) {
  let styles = css``

  if ($horizontallyScrollable) {
    styles = css`
      ${styles}
      display: flex;
      width: ${$numCols * 50}%;
      & > * {
        flex: 1 1 0;
        display: block;
      }
    `
  }

  if ($verticallyScrollable || $horizontallyScrollable) {
    const { width } = theme.borders[$borderVariant]

    styles = css`
      ${styles}
      & > * {
        border-width: ${`calc(${width}/2);`};
      }
    `
  }

  return styles
}

function getCollapsibleForRow({ theme, $collapsible, $borderVariant, $rowLines }) {
  let style = css``
  const { inline } = theme.borders[$borderVariant]

  if ($collapsible === COLLAPSIBLE_ROWS) {
    style = css`
      ${style}
      td:first-child {
        font-weight: ${theme.fonts.weight.bold};
      }
    `
  }

  if ($collapsible !== COLLAPSIBLE_NONE && $rowLines) {
    style = css`
      ${style}
      border-bottom: ${inline};
      &:last-child {
        border-bottom: none;
      }
    `
  }

  return style
}

export const TableHead = styled.thead``
export const TableHeadCells = styled.th``
export const TableDataCells = styled.td``
